import {
  type CreateWorkerShoppableQualificationRequest,
  createWorkerShoppableQualificationRequest,
  workerShoppableQualificationsContract,
} from "@clipboard-health/contract-backend-main";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type z } from "zod";

import { createUseGetWorkerShoppableQualificationsQueryKey } from "./useGetWorkerShoppableQualifications";

const createWorkerShoppableQualificationResponse =
  workerShoppableQualificationsContract.createWorkerShoppableQualification.responses[201];

type CreateWorkerShoppableQualificationResponse = z.infer<
  typeof createWorkerShoppableQualificationResponse
>;

export function useCreateWorkerShoppableQualification(
  workerId: string,
  options: UseMutationOptions<
    CreateWorkerShoppableQualificationResponse,
    unknown,
    CreateWorkerShoppableQualificationRequest
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateWorkerShoppableQualificationRequest) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/shoppable-qualifications`,
        requestSchema: createWorkerShoppableQualificationRequest,
        responseSchema: createWorkerShoppableQualificationResponse,
        data,
      });
      return response.data;
    },
    onSuccess: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries({
        queryKey: createUseGetWorkerShoppableQualificationsQueryKey(workerId),
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_WORKER_SHOPPABLE_QUALIFICATION_FAILURE,
    },
    ...options,
  });
}
